import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { translations } from "@/i18n/translations";
import { api } from "@/lib/api";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("af_lang") || "hi");
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [settings, setSettings] = useState({ maintenance: false });

  const refreshSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/public");
      setSettings(data);
    } catch {}
  }, []);

  useEffect(() => {
    refreshSettings();
    const onMaint = () => setSettings((s) => ({ ...s, maintenance: true }));
    window.addEventListener("af:maintenance", onMaint);
    const id = setInterval(refreshSettings, 60000);
    return () => { window.removeEventListener("af:maintenance", onMaint); clearInterval(id); };
  }, [refreshSettings]);

  const setLang = (l) => {
    setLangState(l);
    localStorage.setItem("af_lang", l);
  };

  const t = translations[lang];

  const pick = useCallback((obj) => {
    if (!obj) return "";
    return obj[lang] ?? obj.en ?? "";
  }, [lang]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setAuthLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  };

  useEffect(() => {
    document.documentElement.classList.toggle("lang-hi", lang === "hi");
  }, [lang]);

  return (
    <AppContext.Provider value={{ lang, setLang, t, pick, user, setUser, authLoading, checkAuth, logout, settings, refreshSettings }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
